import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://clvykdyagubzwcbnbhtw.supabase.co';

interface SamsaraVehicle {
  id: string;
  name: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: string;
  licensePlate?: string;
  tags?: Array<{ id: string; name: string }>;
}

interface SamsaraApiResponse {
  data: SamsaraVehicle[];
  pagination?: {
    hasNextPage: boolean;
    endCursor?: string;
  };
}

/**
 * Sync Samsara vehicles to database - creates new trucks if they don't exist
 */
export async function syncSamsaraVehiclesToDatabase(): Promise<{
  success: boolean;
  added: number;
  updated: number;
  total: number;
  error?: string;
}> {
  try {
    // Fetch all vehicles from Samsara
    const vehicles = await fetchSamsaraVehicles();
    
    if (!vehicles.success || !vehicles.vehicles) {
      return {
        success: false,
        added: 0,
        updated: 0,
        total: 0,
        error: 'Failed to fetch vehicles from Samsara',
      };
    }

    let added = 0;
    let updated = 0;

    // Check each vehicle and add/update in database
    for (const vehicle of vehicles.vehicles) {
      try {
        // Check if truck exists by samsara_id (handle RLS 406 errors)
        const { data: existingTruck, error: selectError } = await supabase
          .from('trucks')
          .select('id, samsara_id')
          .eq('samsara_id', vehicle.id)
          .maybeSingle();

        // If RLS error (406), assume truck doesn't exist and try to insert
        const truckExists = existingTruck && !selectError;

        if (!truckExists) {
          // Create new truck - handle empty/null VINs to avoid duplicates
          const { error: insertError } = await supabase
            .from('trucks')
            .insert({
              unit_number: vehicle.name,
              samsara_id: vehicle.id,
              vin: (vehicle.vin && vehicle.vin.trim()) || null,
              make: vehicle.make || null,
              model: vehicle.model || null,
              year: vehicle.year ? parseInt(vehicle.year) : null,
              license_plate: vehicle.licensePlate || null,
              status: 'in_service',
            });

          if (!insertError) {
            added++;
            console.log(`✅ Added truck: ${vehicle.name} (${vehicle.id})`);
          } else if (insertError.code === '23505') {
            // Duplicate key error - truck already exists with same VIN or samsara_id
            console.log(`⚠️ Skipped duplicate: ${vehicle.name} (${vehicle.id})`);
          } else {
            console.error(`❌ Failed to add truck ${vehicle.name}:`, insertError);
          }
        } else {
        // Update existing truck with latest info
        const { error: updateError } = await supabase
          .from('trucks')
          .update({
            unit_number: vehicle.name,
            vin: vehicle.vin || null,
            make: vehicle.make || null,
            model: vehicle.model || null,
            year: vehicle.year ? parseInt(vehicle.year) : null,
            license_plate: vehicle.licensePlate || null,
          })
          .eq('id', existingTruck.id);

          if (!updateError) {
            updated++;
            console.log(`🔄 Updated truck: ${vehicle.name} (${vehicle.id})`);
          }
        }
      } catch (vehicleError: any) {
        console.error(`Error processing vehicle ${vehicle.name}:`, vehicleError);
      }
    }

    return {
      success: true,
      added,
      updated,
      total: vehicles.vehicles.length,
    };
  } catch (error: any) {
    console.error('Error syncing Samsara vehicles:', error);
    return {
      success: false,
      added: 0,
      updated: 0,
      total: 0,
      error: error.message,
    };
  }
}

/**
 * Sync Samsara trailers to database - creates new trailers if they don't exist
 */
export async function syncSamsaraTrailersToDatabase(): Promise<{
  success: boolean;
  added: number;
  updated: number;
  total: number;
  error?: string;
}> {
  try {
    // Fetch all trailers from Samsara
    const endpoint = '/fleet/trailers';
    const response = await callSamsaraAPI(endpoint);
    
    if (!response || !response.data) {
      return {
        success: false,
        added: 0,
        updated: 0,
        total: 0,
        error: 'Failed to fetch trailers from Samsara',
      };
    }

    let added = 0;
    let updated = 0;
    const trailers = response.data;

    // Check each trailer and add/update in database
    for (const trailer of trailers) {
      // Check if trailer exists by samsara_id
      const { data: existingTrailer } = await supabase
        .from('trailers')
        .select('id, samsara_id')
        .eq('samsara_id', trailer.id)
        .single();

      if (!existingTrailer) {
        // Create new trailer
        const { error: insertError } = await supabase
          .from('trailers')
          .insert({
            unit_number: trailer.name,
            samsara_id: trailer.id,
            make: trailer.make || null,
            model: trailer.model || null,
            year: trailer.year ? parseInt(trailer.year) : null,
            license_plate: trailer.licensePlate || null,
            trailer_type: trailer.trailerType || 'dry_van',
            status: 'available',
          });

        if (!insertError) {
          added++;
          console.log(`✅ Added trailer: ${trailer.name} (${trailer.id})`);
        } else {
          console.error(`❌ Failed to add trailer ${trailer.name}:`, insertError);
        }
      } else {
        // Update existing trailer with latest info
        const { error: updateError } = await supabase
          .from('trailers')
          .update({
            unit_number: trailer.name,
            make: trailer.make || null,
            model: trailer.model || null,
            year: trailer.year ? parseInt(trailer.year) : null,
            license_plate: trailer.licensePlate || null,
          })
          .eq('id', existingTrailer.id);

        if (!updateError) {
          updated++;
          console.log(`🔄 Updated trailer: ${trailer.name} (${trailer.id})`);
        }
      }
    }

    return {
      success: true,
      added,
      updated,
      total: trailers.length,
    };
  } catch (error: any) {
    console.error('Error syncing Samsara trailers:', error);
    return {
      success: false,
      added: 0,
      updated: 0,
      total: 0,
      error: error.message,
    };
  }
}

/**
 * Call Samsara API via Supabase Edge Function (to avoid CORS)
 */
async function callSamsaraAPI(endpoint: string, method: string = 'GET'): Promise<any> {
  // Always use Supabase Edge Function proxy
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/samsara-proxy`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        method,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API request failed: ${response.status}`);
  }

  return await response.json();
}

/**
 * Test Samsara API connection
 */
export async function testSamsaraConnection(): Promise<{ success: boolean; error?: string }> {
  try {
    await callSamsaraAPI('/fleet/vehicles?limit=1');
    return { success: true };
  } catch (error: any) {
    console.error('Error testing Samsara connection:', error);
    return { success: false, error: error.message || 'Failed to connect to Samsara API' };
  }
}

/**
 * Fetch all vehicles from Samsara API
 */
export async function fetchSamsaraVehicles(): Promise<{ 
  success: boolean; 
  vehicles?: SamsaraVehicle[]; 
  error?: string 
}> {
  try {
    const allVehicles: SamsaraVehicle[] = [];
    let hasNextPage = true;
    let endCursor: string | undefined;

    // Fetch all pages
    while (hasNextPage) {
      let endpoint = '/fleet/vehicles?limit=100';
      if (endCursor) {
        endpoint += `&after=${endCursor}`;
      }

      const data: SamsaraApiResponse = await callSamsaraAPI(endpoint);
      allVehicles.push(...data.data);

      hasNextPage = data.pagination?.hasNextPage || false;
      endCursor = data.pagination?.endCursor;
    }

    return { success: true, vehicles: allVehicles };
  } catch (error: any) {
    console.error('Error fetching Samsara vehicles:', error);
    return { success: false, error: error.message || 'Failed to fetch vehicles from Samsara' };
  }
}

/**
 * Import Samsara vehicles into the trucks table
 */
export async function importSamsaraVehicles(): Promise<{ 
  success: boolean; 
  imported: number;
  updated: number;
  skipped: number;
  error?: string 
}> {
  try {
    // Fetch vehicles from Samsara
    const result = await fetchSamsaraVehicles();
    
    if (!result.success || !result.vehicles) {
      return { 
        success: false, 
        imported: 0, 
        updated: 0,
        skipped: 0,
        error: result.error 
      };
    }

    let imported = 0;
    let updated = 0;
    let skipped = 0;

    // Process each vehicle
    for (const vehicle of result.vehicles) {
      try {
        // Check if truck already exists by samsara_id
        const { data: existingTruck } = await supabase
          .from('trucks')
          .select('id, unit_number')
          .eq('samsara_id', vehicle.id)
          .single();

        // Extract unit number from name (e.g., "T-185" or "Truck 185")
        const unitNumberMatch = vehicle.name.match(/(?:T-)?(\d+)/);
        const unitNumber = unitNumberMatch ? `T-${unitNumberMatch[1]}` : vehicle.name;

        const truckData = {
          samsara_id: vehicle.id,
          unit_number: unitNumber,
          vin: vehicle.vin || null,
          make: vehicle.make || null,
          model: vehicle.model || null,
          year: vehicle.year ? parseInt(vehicle.year) : null,
          license_plate: vehicle.licensePlate || null,
          status: 'in_service',
        };

        if (existingTruck) {
          // Update existing truck
          const { error: updateError } = await supabase
            .from('trucks')
            .update(truckData)
            .eq('id', existingTruck.id);

          if (updateError) {
            console.error(`Error updating truck ${vehicle.name}:`, updateError);
            skipped++;
          } else {
            updated++;
          }
        } else {
          // Insert new truck
          const { error: insertError } = await supabase
            .from('trucks')
            .insert(truckData);

          if (insertError) {
            console.error(`Error inserting truck ${vehicle.name}:`, insertError);
            skipped++;
          } else {
            imported++;
          }
        }
      } catch (error) {
        console.error(`Error processing vehicle ${vehicle.name}:`, error);
        skipped++;
      }
    }

    return { 
      success: true, 
      imported, 
      updated,
      skipped 
    };
  } catch (error: any) {
    console.error('Error importing Samsara vehicles:', error);
    return { 
      success: false, 
      imported: 0, 
      updated: 0,
      skipped: 0,
      error: error.message || 'Failed to import vehicles' 
    };
  }
}

/**
 * Fetch all assets (trailers) from Samsara API
 */
async function fetchSamsaraAssets(): Promise<{ 
  success: boolean; 
  assets?: SamsaraVehicle[]; 
  error?: string 
}> {
  try {
    const allAssets: SamsaraVehicle[] = [];
    let hasNextPage = true;
    let endCursor: string | undefined;

    // Fetch all pages from assets endpoint
    while (hasNextPage) {
      let endpoint = '/assets?limit=100';
      if (endCursor) {
        endpoint += `&after=${endCursor}`;
      }

      const data: SamsaraApiResponse = await callSamsaraAPI(endpoint);
      allAssets.push(...data.data);

      hasNextPage = data.pagination?.hasNextPage || false;
      endCursor = data.pagination?.endCursor;
    }

    return { success: true, assets: allAssets };
  } catch (error: any) {
    console.error('Error fetching Samsara assets:', error);
    return { success: false, error: error.message || 'Failed to fetch assets from Samsara' };
  }
}

/**
 * Import Samsara trailers into the trailers table
 */
export async function importSamsaraTrailers(): Promise<{ 
  success: boolean; 
  imported: number;
  updated: number;
  skipped: number;
  error?: string 
}> {
  try {
    // Fetch assets (trailers) from Samsara
    const result = await fetchSamsaraAssets();
    
    if (!result.success || !result.assets) {
      return { 
        success: false, 
        imported: 0, 
        updated: 0,
        skipped: 0,
        error: result.error 
      };
    }

    let imported = 0;
    let updated = 0;
    let skipped = 0;

    // Filter for trailers only (exclude trucks)
    const trailers = result.assets.filter(asset => 
      asset.name && (
        asset.name.toUpperCase().includes('TRAILER') || 
        asset.name.toUpperCase().includes('TR-')
      )
    );

    console.log(`Found ${result.assets.length} assets from Samsara, ${trailers.length} are trailers`);

    // Process each trailer
    for (const trailer of trailers) {
      try {
        // Check if trailer already exists by samsara_id
        const { data: existingTrailer } = await supabase
          .from('trailers')
          .select('id, unit_number')
          .eq('samsara_id', trailer.id)
          .single();

        // Extract unit number from name (e.g., "TR-185" or "Trailer 185")
        const unitNumberMatch = trailer.name.match(/(?:TR-)?(\d+)/i);
        const unitNumber = unitNumberMatch ? `TR-${unitNumberMatch[1]}` : trailer.name;

        const trailerData = {
          samsara_id: trailer.id,
          unit_number: unitNumber,
          vin: trailer.vin || null,
          make: trailer.make || null,
          model: trailer.model || null,
          year: trailer.year ? parseInt(trailer.year) : null,
          license_plate: trailer.licensePlate || null,
          license_state: null,
          status: 'available',
          trailer_type: 'dry_van', // Default type for Samsara imports
          length_feet: 53, // Default 53ft dry van
          capacity_weight: 45000, // Default 45,000 lbs capacity
        };

        if (existingTrailer) {
          // Update existing trailer
          const { error: updateError } = await supabase
            .from('trailers')
            .update(trailerData)
            .eq('id', existingTrailer.id);

          if (updateError) {
            console.error(`Error updating trailer ${trailer.name}:`, updateError);
            skipped++;
          } else {
            updated++;
          }
        } else {
          // Insert new trailer
          const { error: insertError } = await supabase
            .from('trailers')
            .insert(trailerData);

          if (insertError) {
            console.error(`Error inserting trailer ${trailer.name}:`, insertError);
            skipped++;
          } else {
            imported++;
          }
        }
      } catch (error) {
        console.error(`Error processing trailer ${trailer.name}:`, error);
        skipped++;
      }
    }

    return { 
      success: true, 
      imported, 
      updated,
      skipped 
    };
  } catch (error: any) {
    console.error('Error importing Samsara trailers:', error);
    return { 
      success: false, 
      imported: 0, 
      updated: 0,
      skipped: 0,
      error: error.message || 'Failed to import trailers' 
    };
  }
}

/**
 * Get real-time vehicle locations from Samsara
 */
export async function getVehicleLocations(): Promise<{
  success: boolean;
  locations?: Array<{
    vehicleId: string;
    latitude: number;
    longitude: number;
    heading?: number;
    speed?: number;
    time: string;
  }>;
  error?: string;
}> {
  try {
    const allLocations: any[] = [];
    let hasNextPage = true;
    let endCursor: string | undefined;

    // Fetch all pages to get all 147 vehicles
    while (hasNextPage) {
      let endpoint = '/fleet/vehicles/locations?limit=100';
      if (endCursor) {
        endpoint += `&after=${endCursor}`;
      }

      const data = await callSamsaraAPI(endpoint);
      
      if (data && data.data) {
        allLocations.push(...data.data);
      }

      hasNextPage = data.pagination?.hasNextPage || false;
      endCursor = data.pagination?.endCursor;
    }
    
    // Removed excessive logging for performance
    // console.log(`Fetched ${allLocations.length} vehicle locations from Samsara API`);
    
    // Removed excessive logging for performance
    // if (allLocations.length > 0) {
    //   console.log('Sample Samsara location data:', JSON.stringify(allLocations[0], null, 2));
    // }
    
    return { 
      success: true, 
      locations: allLocations.map((loc: any) => {
        // Samsara API returns location data in a nested structure
        const location = loc.location || loc;
        
        // Samsara API returns speed in mph in the 'speed' field
        // Both 'speedMilesPerHour' and 'speed' are already in mph - no conversion needed
        let speedMph = 0;
        if (location.speedMilesPerHour !== undefined && location.speedMilesPerHour !== null) {
          speedMph = location.speedMilesPerHour;
        } else if (location.speed !== undefined && location.speed !== null) {
          // Speed is already in mph - use directly without conversion
          speedMph = location.speed;
        }
        
        return {
          vehicleId: loc.id,
          latitude: location.latitude,
          longitude: location.longitude,
          heading: location.heading,
          speed: Math.round(speedMph), // Speed in mph, rounded
          time: location.time || loc.time || new Date().toISOString(),
        };
      })
    };
  } catch (error: any) {
    console.error('Error fetching vehicle locations:', error);
    return { success: false, error: error.message || 'Failed to fetch vehicle locations' };
  }
}

/**
 * Get comprehensive vehicle diagnostics from Samsara
 * Fetches engine hours, odometer, fuel, DEF, battery voltage, check engine light, and active faults
 */
/**
 * Get vehicle diagnostics from database (webhook data)
 * This uses data collected by webhooks instead of calling the API directly
 */
export async function getVehicleDiagnosticsFromDatabase(truckId: string): Promise<{
  success: boolean;
  diagnostics?: {
    odometer?: number;
    engineHours?: number;
    fuelPercent?: number;
    defPercent?: number;
    batteryVoltage?: number;
    checkEngineLight?: 'none' | 'amber' | 'red';
    faults?: Array<{
      id: string;
      severity: 'low' | 'medium' | 'high';
      description: string;
      spn: number;
      fmi: number;
      count: number;
      activeSince?: string;
    }>;
  };
  error?: string;
}> {
  try {
    // Fetch latest diagnostics from database
    const { data: latestDiag, error: diagError } = await supabase
      .from('vehicle_diagnostics')
      .select('*')
      .eq('truck_id', truckId)
      .order('recorded_at', { ascending: false })
      .limit(1)
      .single();

    // Fetch active fault codes
    const { data: faultCodes, error: faultsError } = await supabase
      .from('vehicle_fault_codes')
      .select('*')
      .eq('truck_id', truckId)
      .is('resolved_at', null)
      .order('last_occurred_at', { ascending: false });

    if (diagError && diagError.code !== 'PGRST116') {
      // PGRST116 is "no rows returned" which is ok
      console.log('Database query error (expected if no data yet):', diagError.code, diagError.message);
    }

    if (faultsError) {
      console.log('Fault codes query error:', faultsError);
    }
    
    console.log('Database diagnostics:', latestDiag);
    console.log('Database faults:', faultCodes);

    // Build diagnostics object
    const diagnostics: any = {
      faults: [],
      checkEngineLight: 'none',
    };

    if (latestDiag) {
      diagnostics.odometer = latestDiag.odometer_miles;
      diagnostics.engineHours = latestDiag.engine_hours_decimal;
      diagnostics.fuelPercent = latestDiag.fuel_percent;
      diagnostics.defPercent = latestDiag.def_percent;
      diagnostics.batteryVoltage = latestDiag.battery_voltage;
      diagnostics.checkEngineLight = latestDiag.check_engine_light || 'none';
    }

    if (faultCodes && faultCodes.length > 0) {
      diagnostics.faults = faultCodes.map((fault: any) => ({
        id: fault.id,
        severity: fault.severity,
        description: fault.description,
        spn: fault.spn,
        fmi: fault.fmi,
        count: fault.occurrence_count,
        activeSince: fault.first_occurred_at,
      }));

      // Update check engine light based on faults if not already set
      if (!latestDiag || !latestDiag.check_engine_light) {
        const hasHighSeverity = faultCodes.some((f: any) => f.severity === 'high');
        const hasMediumSeverity = faultCodes.some((f: any) => f.severity === 'medium');
        
        if (hasHighSeverity) {
          diagnostics.checkEngineLight = 'red';
        } else if (hasMediumSeverity || faultCodes.length > 0) {
          diagnostics.checkEngineLight = 'amber';
        }
      }
    }

    return {
      success: true,
      diagnostics,
    };
  } catch (error: any) {
    console.error('Error getting vehicle diagnostics from database:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Get vehicle diagnostics from Samsara API (legacy method)
 * Note: This fetches data directly from API which may be slower than webhook data
 */
export async function getVehicleDiagnostics(samsaraVehicleId: string): Promise<{
  success: boolean;
  diagnostics?: {
    odometer?: number; // miles
    engineHours?: number; // hours
    fuelPercent?: number; // percentage
    defPercent?: number; // DEF level percentage
    batteryVoltage?: number; // volts
    checkEngineLight?: 'none' | 'amber' | 'red';
    faults?: Array<{
      id: string;
      severity: 'low' | 'medium' | 'high';
      description: string;
      spn: number;
      fmi: number;
      count: number;
      activeSince?: string;
    }>;
  };
  error?: string;
}> {
  try {
    const diagnostics: any = {
      faults: [],
      checkEngineLight: 'none',
    };

    // Fetch vehicle stats using the snapshot endpoint with specific types
    // NOTE: Samsara API restricts to 4 types per request, so we need to make multiple calls
    
    // First call: Get odometer, engine hours, fuel, and DEF
    const statsTypes1 = 'obdOdometerMeters,obdEngineSeconds,fuelPercents,defLevelMilliPercent';
    const statsEndpoint1 = `/fleet/vehicles/stats?vehicleIds=${samsaraVehicleId}&types=${statsTypes1}`;
    const statsData1 = await callSamsaraAPI(statsEndpoint1);
    
    // Second call: Get battery and fault codes
    const statsTypes2 = 'batteryMilliVolts,faultCodes';
    const statsEndpoint2 = `/fleet/vehicles/stats?vehicleIds=${samsaraVehicleId}&types=${statsTypes2}`;
    const statsData2 = await callSamsaraAPI(statsEndpoint2);
    
    console.log('Vehicle stats data 1:', statsData1);
    console.log('Vehicle stats data 2:', statsData2);
    
    // Log the actual vehicle data if found
    if (statsData1?.data && statsData1.data.length > 0) {
      const vehicle1 = statsData1.data.find((v: any) => v.id === samsaraVehicleId);
      console.log('Vehicle data from call 1:', vehicle1);
    }
    if (statsData2?.data && statsData2.data.length > 0) {
      const vehicle2 = statsData2.data.find((v: any) => v.id === samsaraVehicleId);
      console.log('Vehicle data from call 2:', vehicle2);
    }
    
    // Parse stats data from first call - response has a 'data' array with vehicle objects
    if (statsData1 && statsData1.data && Array.isArray(statsData1.data)) {
      const vehicleData = statsData1.data.find((v: any) => v.id === samsaraVehicleId);
      
      if (vehicleData) {
        // Parse odometer (meters to miles) - check both singular and plural forms
        const odometerField = vehicleData.obdOdometerMeters || vehicleData.obdOdometerMeter;
        if (odometerField && typeof odometerField === 'object' && odometerField.value !== undefined) {
          diagnostics.odometer = Math.round(odometerField.value * 0.000621371);
          console.log('Parsed odometer:', diagnostics.odometer);
        }
        
        // Parse engine hours (seconds to hours)
        const engineField = vehicleData.obdEngineSeconds || vehicleData.obdEngineSecond;
        if (engineField && typeof engineField === 'object' && engineField.value !== undefined) {
          diagnostics.engineHours = Math.round((engineField.value / 3600) * 100) / 100;
          console.log('Parsed engine hours:', diagnostics.engineHours);
        }
        
        // Parse fuel percentage - check both singular and plural forms
        const fuelField = vehicleData.fuelPercent || vehicleData.fuelPercents;
        if (fuelField && typeof fuelField === 'object' && fuelField.value !== undefined) {
          diagnostics.fuelPercent = Math.round(fuelField.value);
          console.log('Parsed fuel:', diagnostics.fuelPercent);
        }
        
        // Parse DEF level (milliPercent to percent)
        const defField = vehicleData.defLevelMilliPercent;
        if (defField && typeof defField === 'object' && defField.value !== undefined) {
          diagnostics.defPercent = Math.round(defField.value / 10);
          console.log('Parsed DEF:', diagnostics.defPercent);
        }
      }
    }
    
    // Parse stats data from second call (battery and fault codes)
    if (statsData2 && statsData2.data && Array.isArray(statsData2.data)) {
      const vehicleData2 = statsData2.data.find((v: any) => v.id === samsaraVehicleId);
      
      if (vehicleData2) {
        // Parse battery voltage (millivolts to volts)
        const batteryField = vehicleData2.batteryMilliVolts || vehicleData2.batteryMilliVolt;
        if (batteryField && typeof batteryField === 'object' && batteryField.value !== undefined) {
          diagnostics.batteryVoltage = Math.round((batteryField.value / 1000) * 1000) / 1000;
          console.log('Parsed battery:', diagnostics.batteryVoltage);
        }
        
        // Parse fault codes
        const faultField = vehicleData2.faultCodes || vehicleData2.faultCode;
        if (faultField && typeof faultField === 'object') {
          const faultReading = faultField;
          
          // Check for check engine light status
          if (faultReading.value) {
            const j1939Data = faultReading.value.j1939 || faultReading.value;
            
            // Determine check engine light status
            if (j1939Data.diagnosticTroubleCodes && j1939Data.diagnosticTroubleCodes.length > 0) {
              // Check for red stop lamp or amber warning lamp
              if (j1939Data.redStopLampOn) {
                diagnostics.checkEngineLight = 'red';
              } else if (j1939Data.amberWarningLampOn || j1939Data.malfunctionIndicatorLampOn) {
                diagnostics.checkEngineLight = 'amber';
              }
              
              // Parse fault codes
              diagnostics.faults = j1939Data.diagnosticTroubleCodes.map((dtc: any) => {
                // Determine severity
                let severity: 'low' | 'medium' | 'high' = 'low';
                if (dtc.occurrenceCount > 10 || j1939Data.redStopLampOn) {
                  severity = 'high';
                } else if (dtc.occurrenceCount > 3 || j1939Data.amberWarningLampOn) {
                  severity = 'medium';
                }
                
                return {
                  id: dtc.id || `${dtc.spnId}-${dtc.fmiId}`,
                  severity,
                  description: dtc.spnDescription || `SPN ${dtc.spnId} - FMI ${dtc.fmiId}`,
                  spn: dtc.spnId || 0,
                  fmi: dtc.fmiId || 0,
                  count: dtc.occurrenceCount || 1,
                  activeSince: dtc.fmiText || undefined,
                };
              });
            }
          }
        }
      }
    }

    return {
      success: true,
      diagnostics,
    };
  } catch (error: any) {
    console.error('Error getting vehicle diagnostics:', error);
    return {
      success: true,
      diagnostics: {
        faults: [],
        checkEngineLight: 'none',
      },
    };
  }
}

/**
 * Get vehicle stats for a date range (for IFTA reporting)
 * Uses existing truck_mileage_logs data (best), webhook data, or Samsara API
 */
export async function getVehicleStatsForIFTA(
  samsaraVehicleId: string, 
  startDate: Date, 
  endDate: Date
): Promise<{
  success: boolean;
  totalMiles?: number;
  totalFuel?: number;
  avgMPG?: number;
  error?: string;
}> {
  try {
    console.log('Getting IFTA stats for vehicle:', samsaraVehicleId, 'from', startDate, 'to', endDate);
    
    // First, get truck_id from samsara_id
    const { data: truck, error: truckError } = await supabase
      .from('trucks')
      .select('id')
      .eq('samsara_id', samsaraVehicleId)
      .single();

    if (truckError) {
      console.error('Error finding truck:', truckError);
    }

    let totalMiles = 0;
    
    // Try Method 1: Use existing truck_mileage_logs (BEST - already has trip data!)
    if (truck) {
      const { data: mileageLogs, error: logsError } = await supabase
        .from('truck_mileage_logs')
        .select('total_miles, loaded_miles, empty_miles, trip_start_date, trip_end_date')
        .eq('truck_id', truck.id)
        .gte('trip_start_date', startDate.toISOString())
        .lte('trip_end_date', endDate.toISOString());

      if (logsError) {
        console.log('No mileage logs data:', logsError.message);
      }

      if (mileageLogs && mileageLogs.length > 0) {
        // Sum up all trips in the date range
        totalMiles = mileageLogs.reduce((sum, log) => sum + (log.total_miles || 0), 0);
        console.log(`✅ Calculated ${totalMiles} miles from ${mileageLogs.length} trip(s) in truck_mileage_logs`);
      }
    }
    
    // Try Method 2: Get vehicle diagnostics data from webhook (odometer readings)
    if (totalMiles === 0) {
      const { data: diagnosticsData, error: diagError } = await supabase
        .from('vehicle_diagnostics')
        .select('odometer_miles, recorded_at')
        .eq('samsara_vehicle_id', samsaraVehicleId)
        .gte('recorded_at', startDate.toISOString())
        .lte('recorded_at', endDate.toISOString())
        .order('recorded_at', { ascending: true });

      if (diagError) {
        console.log('No webhook diagnostics data:', diagError.message);
      }

      if (diagnosticsData && diagnosticsData.length >= 2) {
        const firstReading = diagnosticsData[0];
        const lastReading = diagnosticsData[diagnosticsData.length - 1];
        
        if (firstReading.odometer_miles && lastReading.odometer_miles) {
          totalMiles = lastReading.odometer_miles - firstReading.odometer_miles;
          console.log(`✅ Calculated miles from webhook odometer: ${totalMiles} miles`);
        }
      }
    }
    
    // Try Method 3: Calculate from GPS locations (webhook)
    if (totalMiles === 0) {
      console.log('No webhook odometer data, trying GPS locations...');
      
      const { data: locationData, error: locError } = await supabase
        .from('vehicle_locations')
        .select('latitude, longitude, recorded_at')
        .eq('samsara_vehicle_id', samsaraVehicleId)
        .gte('recorded_at', startDate.toISOString())
        .lte('recorded_at', endDate.toISOString())
        .order('recorded_at', { ascending: true });

      if (locError) {
        console.log('No webhook location data:', locError.message);
      }

      if (locationData && locationData.length >= 2) {
        totalMiles = calculateTotalDistanceFromLocations(locationData);
        console.log(`✅ Calculated miles from webhook GPS: ${totalMiles} miles`);
      }
    }
    
    // Try Method 4: Call Samsara API directly (fallback)
    if (totalMiles === 0) {
      console.log('No local data, calling Samsara API directly...');
      
      try {
        const startTime = startDate.toISOString();
        const endTime = endDate.toISOString();
        
        // Get vehicle stats from Samsara API
        const endpoint = `/fleet/vehicles/stats?vehicleIds=${samsaraVehicleId}&startTime=${startTime}&endTime=${endTime}&types=obdOdometerMeters`;
        const response = await callSamsaraAPI(endpoint);
        
        if (response && response.data && response.data.length > 0) {
          const vehicleData = response.data.find((v: any) => v.id === samsaraVehicleId);
          
          if (vehicleData && vehicleData.obdOdometerMeters) {
            const readings = vehicleData.obdOdometerMeters;
            if (Array.isArray(readings) && readings.length >= 2) {
              const startOdometerMeters = readings[0].value || 0;
              const endOdometerMeters = readings[readings.length - 1].value || 0;
              const totalMeters = endOdometerMeters - startOdometerMeters;
              totalMiles = totalMeters * 0.000621371; // Convert meters to miles
              console.log(`✅ Calculated miles from Samsara API: ${totalMiles} miles`);
            }
          }
        }
      } catch (apiError: any) {
        console.error('Samsara API error:', apiError.message);
      }
    }
    
    // If still no data, return error
    if (totalMiles === 0) {
      return {
        success: false,
        error: 'No mileage data available for this period. The vehicle may not have been active during this time.'
      };
    }
    
    // Estimate fuel based on average MPG for commercial trucks
    const estimatedMPG = 6.5;
    const totalFuel = totalMiles / estimatedMPG;
    
    return {
      success: true,
      totalMiles: Math.round(totalMiles * 100) / 100,
      totalFuel: Math.round(totalFuel * 100) / 100,
      avgMPG: estimatedMPG
    };
  } catch (error: any) {
    console.error('Error getting vehicle stats for IFTA:', error);
    return {
      success: false,
      error: error.message || 'Failed to get vehicle stats'
    };
  }
}

/**
 * Calculate total distance from GPS location points using Haversine formula
 */
function calculateTotalDistanceFromLocations(locations: Array<{ latitude: number; longitude: number }>): number {
  let totalMiles = 0;
  
  for (let i = 1; i < locations.length; i++) {
    const prev = locations[i - 1];
    const curr = locations[i];
    
    // Haversine formula to calculate distance between two GPS points
    const R = 3959; // Earth's radius in miles
    const dLat = toRadians(curr.latitude - prev.latitude);
    const dLon = toRadians(curr.longitude - prev.longitude);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRadians(prev.latitude)) * Math.cos(toRadians(curr.latitude)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    totalMiles += distance;
  }
  
  return totalMiles;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}
